import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, Package, QrCode, ShoppingCart, User } from 'lucide-react';

export const retailProductAssistant: Playbook = {
  meta: {
    id: 'retail-product-assistant',
    slug: 'retail-product-assistant',
    categoryId: 'physical-world',
    name: 'The Retail Product Genie',
    tagline: 'Turn every product into a sales associate.',
    description: 'Place QR codes on products, shelves, or displays. Customers scan to get instant product information, comparisons, availability, and personalized recommendations.',
    strategy: 'Place QR codes on products, shelves, or displays. Customers scan to get instant product information, comparisons, availability, and personalized recommendations without waiting for staff.',
    benefits: [
      'Instant product information access',
      'Reduce customer wait times',
      'Increase sales conversions',
      'Staff efficiency improvement'
    ],
    targetAudience: ['Retail stores', 'Electronics retailers', 'Home improvement stores'],
    estimatedSetupTime: '25 minutes',
    difficulty: 'beginner',
    tags: ['retail', 'qr-codes', 'product-info', 'in-store'],
    isFeatured: true
  },
  seo: {
    metaTitle: 'Retail Product Genie Playbook - QR Code Shopping Experience',
    metaDescription: 'Transform retail with QR code product Genies. Instant information, comparisons, and recommendations for every product.',
    keywords: ['retail Genie', 'qr code shopping', 'product information', 'in-store experience', 'retail Genie']
  },
  demoConfig: {
    triggerType: 'immediate',
    message: "👋 Hi! I see you're interested in our SmartHome Security System. I can answer any questions about features, installation, or help you compare models. What would you like to know?",
    position: 'center'
  },
  demoScript: [
    { speaker: 'agent', message: "Hi! I'm here to help with this SmartHome Security System. What would you like to know?" },
    { speaker: 'user', message: "Does it work with Alexa?" },
    { speaker: 'agent', message: "Yes! It's fully compatible with Alexa, Google Home, and Apple HomeKit." },
    { speaker: 'user', message: "Is it easy to install myself?" },
    { speaker: 'agent', message: "Absolutely! Most customers install it in under 30 minutes. I can send installation videos to your phone if you'd like." }
  ],
  templateConfig: {
    firstMessage: "👋 Hi! I can help you learn about [PRODUCT]. What would you like to know?",
    systemPrompt: "You are a retail product Genie accessed via QR code in a physical store. Help customers understand product features, compare options, check inventory, and make purchasing decisions. Be knowledgeable and helpful.",
    tools: ['product_database', 'inventory_check', 'comparison_tool', 'email_capture', 'store_locator']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'Customer scans QR code',
      description: 'Shopper scans QR code on product, shelf display, or promotional material'
    },
    {
      type: 'genie',
      icon: QrCode,
      genieIcon: 'genie-lamp',
      label: 'Identifies product context',
      description: 'Genie instantly knows which product customer is interested in and location'
    },
    {
      type: 'action',
      icon: Package,
      genieIcon: 'database',
      label: 'Provides detailed information',
      description: 'Shares specs, features, compatibility, pricing, and availability instantly'
    },
    {
      type: 'action',
      icon: Bot,
      genieIcon: 'settings',
      label: 'Offers comparisons',
      description: 'Compares with similar products, explains differences, pros and cons'
    },
    {
      type: 'action',
      icon: ShoppingCart,
      genieIcon: 'settings',
      label: 'Facilitates purchase decision',
      description: 'Provides reviews, installation help, warranty info, and purchase options'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Informed purchase decision',
      description: 'Customer makes confident purchase with complete product knowledge'
    }
  ],
  metrics: {
    conversionRate: '31%',
    responseTime: '<1 second',
    satisfactionScore: '4.6/5',
    costSavings: '$50k/month',
    timeToValue: 'Immediate'
  },
  testimonials: [
    {
      companyName: 'TechZone Electronics',
      authorName: 'Steve Williams',
      authorTitle: 'Store Manager',
      quote: 'QR code Genies increased our conversion rate by 28%. Customers love getting instant answers without waiting for staff.',
      metrics: '28% increase in conversion rate'
    }
  ],
  resources: [
    {
      type: 'guide',
      title: 'QR Code Placement Strategy',
      description: 'Best practices for positioning QR codes in retail environments',
      url: '/docs/qr-placement-retail'
    },
    {
      type: 'template',
      title: 'Product Information Templates',
      description: 'Structured formats for different product categories and information types',
      url: '/templates/product-information'
    }
  ]
};